import React from "react";
import { useTranslation } from "react-i18next";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import colorsStore from "shared/store/colorsStore";
import { formatItalianNumber } from "utils/formatUtils";

type EsgDistributionData = {
  [key: string]: { alta: number; media: number; bassa: number };
};

type EsgDistributionProps = { data: EsgDistributionData };

const EsgDistributionChart: React.FC<EsgDistributionProps> = ({ data }) => {
  const { t } = useTranslation();
  const colorsConfig = colorsStore((state) => state.colorsConfigStore);

  const mapStrings = {
    lettera_a: "Lettera A",
    lettera_b: "Lettera B",
    lettera_c: "Lettera C",
    pai_e: "PAI E",
    pai_g: "PAI G",
    pai_s: "PAI S",
  };
  const formattedData = Object.entries(data).map(([key, values]) => ({
    name: mapStrings[key],
    alta: values.alta * 100,
    media: (values.media - values.alta) * 100,
    bassa: (values.bassa - values.media) * 100,

    total_alta: values.alta * 100,
    total_media: values.media * 100,
    total_bassa: values.bassa * 100,
  }));

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      const dataPoint = payload[0].payload;
      return (
        <div
          className="custom-tooltip"
          style={{
            backgroundColor: "white",
            padding: "10px",
            border: "1px solid #ccc",
            borderRadius: "8px",
            boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
            fontSize: 12,
          }}
        >
          <p>{dataPoint.name}</p>
          <p style={{ color: colorsConfig.BlueDark }}>
            {t("global.high")}: {formatItalianNumber(dataPoint.total_alta, 1)}%
          </p>
          <p style={{ color: colorsConfig.Green }}>
            {t("global.medium")}: {formatItalianNumber(dataPoint.total_media, 1)}%
          </p>
          <p style={{ color: colorsConfig.BlueLight }}>
            {t("global.low")}: {formatItalianNumber(dataPoint.total_bassa, 1)}%
          </p>
        </div>
      );
    }
    return null;
  };
  const CustomBar = (props) => {
    const { x, y, width, height, fill, index, dataKey } = props;

    // Get the current row's data
    const rowData = formattedData[index];

    // Check which values exist in this row
    const hasAlta = rowData.alta > 0;
    const hasMedia = rowData.media > 0;
    const hasBassa = rowData.bassa > 0;

    // Determine if this is the last bar
    let isLastBar = false;
    if (dataKey === "bassa" && hasBassa) {
      isLastBar = true;
    } else if (dataKey === "media" && hasMedia && !hasBassa) {
      isLastBar = true;
    } else if (dataKey === "alta" && hasAlta && !hasMedia && !hasBassa) {
      isLastBar = true;
    }

    const radius = isLastBar ? 4 : 0;

    return (
      <path
        d={`
      M ${x},${y}
      h ${width - radius}
      q ${radius},0 ${radius},${radius}
      v ${height - 2 * radius}
      q 0,${radius} -${radius},${radius}
      h ${-(width - radius)}
      Z
    `}
        fill={fill}
      />
    );
  };
  // Calculate the maximum value dynamically
  const calculateMaxValue = () => {
    let maxTotal = 0;
    formattedData.forEach((item) => {
      const total =
        item.total_alta +
        (item.total_media - item.total_alta) +
        (item.total_bassa - item.total_media);
      if (total > maxTotal) {
        maxTotal = total;
      }
    });

    // Round up to the nearest 10
    return Math.ceil(maxTotal / 10) * 10;
  };

  // Generate ticks dynamically with step of 10
  const generateTicks = () => {
    const maxValue = calculateMaxValue();
    const ticks = [];
    for (let i = 0; i <= maxValue; i += 10) {
      ticks.push(i);
    }
    return ticks;
  };

  const dynamicTicks = generateTicks();
  const renderLegendText = (value, entry) => {
    return (
      <span style={{ marginRight: 38, color: colorsConfig.FinecoBlack }}>
        {value}
      </span>
    );
  };
  return (
    <div className="h-[310px] w-full" style={{ marginTop: 1 }}>
      <ResponsiveContainer width="100%" height="100%" debounce={500}>
        <BarChart
          data={formattedData}
          layout="vertical"
          margin={{ top: 20, right: 20, left: 120, bottom: 0 }}
          barSize={26}
        >
          <CartesianGrid
            strokeDasharray="3 3"
            horizontal={false}
            vertical={false}
          />
          <XAxis
            type="number"
            domain={[0, dynamicTicks[dynamicTicks.length - 1]]}
            tick={{ fontSize: 12, fill: colorsConfig.FinecoBlack }}
            tickFormatter={(value) => `${value}%`}
            tickLine={false}
            axisLine={{ stroke: colorsConfig.Gray3 }}
            ticks={dynamicTicks}
          />
          <YAxis
            dataKey="name"
            type="category"
            tick={{
              width: 80,
              fontSize: 14,
              textAnchor: "end",
              fill: colorsConfig.FinecoBlack,
            }}
            tickLine={false}
            axisLine={{ stroke: colorsConfig.Gray3 }}
            width={80}
          />
          <Tooltip content={<CustomTooltip />} />
          <Legend
            verticalAlign="bottom"
            align="left"
            layout="horizontal"
            wrapperStyle={{
              fontSize: "12px",
              fontFamily: "inherit",
              left: "6px",
              bottom: "-6px",
              paddingTop: "8px",
              paddingBottom: "8px",
            }}
            // formatter={(value, entry) => (
            //   <span style={{ color: colorsConfig.finecoBlack }}>{value}</span>
            // )}
            iconSize={10}
            formatter={renderLegendText}
            iconType="circle"
          />
          <Bar
            dataKey="alta"
            fill={colorsConfig.BlueDark}
            stackId="stack"
            name={t("global.high")}
            shape={<CustomBar />}
            isAnimationActive={false}
          />
          <Bar
            dataKey="media"
            fill={colorsConfig.Green}
            stackId="stack"
            name={t("global.medium")}
            shape={<CustomBar />}
            isAnimationActive={false}
          />
          <Bar
            dataKey="bassa"
            fill={colorsConfig.BlueLight}
            stackId="stack"
            name={t("global.low")}
            shape={<CustomBar />}
            isAnimationActive={false}
          />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

export default EsgDistributionChart;
